import React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import DriverSelect from '@features/drivers/components/DriverSelect';
import { useGetServiceTechniciansQuery } from '../queries/getServiceTechnicians.query.generated';
import { useRooftop, useUser } from '@store';

interface ReassignDriverDialogProps {
  open: boolean;
  onClose: () => void;
  runId: number;
  /** The driver currently assigned to the run. Filtered out of the picker so
   *  the coordinator can't reassign to the same person. */
  currentDriverId: number | null;
  /** Shown in the dialog body as context ("Reassigning from {name}…"). */
  currentDriverName?: string | null;
}

const ReassignDriverDialog: React.FC<ReassignDriverDialogProps> = ({
  open,
  onClose,
  runId,
  currentDriverId,
  currentDriverName,
}) => {
  const user = useUser();
  const rooftop = useRooftop();
  const customerId = rooftop?.id || user?.profile?.customer_id || 0;

  // Same dialog covers two modes — straight reassign (driver exists) and
  // first-dispatch from a 'draft' run (no driver yet). Copy adapts so the
  // coordinator isn't told "reassigning from null". Server endpoint and
  // mechanic are identical.
  const isFirstDispatch = currentDriverId == null;
  const titleText = isFirstDispatch ? 'Dispatch run' : 'Reassign technician';
  const actionLabel = isFirstDispatch ? 'Dispatch' : 'Reassign';
  const inFlightLabel = isFirstDispatch ? 'Dispatching…' : 'Reassigning…';
  const successVerb = isFirstDispatch ? 'dispatched' : 'reassigned';

  const [selectedDriverId, setSelectedDriverId] = React.useState<number | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  // Reuses the same drivers query BuildRunDialog uses for initial dispatch —
  // service technicians scoped to the active rooftop.
  const { data: driversData, loading: driversLoading } = useGetServiceTechniciansQuery({
    variables: { customerId },
    skip: !customerId || !open,
  });

  const drivers = React.useMemo(() => {
    const all = driversData?.drivers ?? [];
    return currentDriverId != null ? all.filter(d => d.id !== currentDriverId) : all;
  }, [driversData, currentDriverId]);

  // Reset selection whenever the dialog reopens — a stale selection from a
  // prior open would surprise the coordinator on the next reassign.
  React.useEffect(() => {
    if (open) setSelectedDriverId(null);
  }, [open]);

  const handleConfirm = async () => {
    if (!selectedDriverId) {
      toast.warning(`Select a technician to ${actionLabel.toLowerCase()} to`);
      return;
    }
    setSubmitting(true);
    try {
      const token = await user?.getToken();
      if (!token) {
        throw new Error('Authentication failed — could not get token');
      }
      const baseUrl = window.location.origin;
      const res = await axios({
        method: 'POST',
        url: `${baseUrl}/.netlify/functions/reassignMobileServiceRun`,
        data: { runId, newDriverId: selectedDriverId },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.data?.success) {
        // Server returned 200 with success:false (unusual; treat as error).
        throw new Error(res.data?.error || `${actionLabel} returned non-success`);
      }
      const newDriver = drivers.find(d => d.id === selectedDriverId);
      toast.success(
        `Run ${successVerb} to ${newDriver?.user?.display_name || `driver #${selectedDriverId}`}`,
      );
      onClose();
    } catch (err: any) {
      // The server returns 409 with a friendly message when the run already
      // started (in_transit etc.). Pass it through verbatim instead of the
      // generic axios "Request failed with status code 409".
      const serverMsg = err?.response?.data?.error;
      toast.error(serverMsg || err?.message || `${actionLabel} failed`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{titleText}</DialogTitle>
      <DialogContent dividers>
        {currentDriverName ? (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Reassigning from <strong>{currentDriverName}</strong>. The current
            technician will be notified that the run was taken off their list, and
            the new technician will receive a fresh offer.
          </Typography>
        ) : (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Pick a technician to dispatch this run to. They'll receive a targeted
            offer and the run will move to <strong>dispatched</strong> until they accept.
          </Typography>
        )}
        <Box sx={{ minHeight: 320 }}>
          <DriverSelect
            drivers={drivers}
            selectedDriverId={selectedDriverId}
            onSelect={id => setSelectedDriverId(id)}
            loading={driversLoading}
            label='Select a technician'
            filterPlaceholder='Search technicians...'
            emptyMessage='No available technicians'
            maxHeight={360}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleConfirm}
          disabled={submitting || !selectedDriverId}
          startIcon={submitting ? <CircularProgress size={16} color='inherit' /> : undefined}>
          {submitting ? inFlightLabel : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReassignDriverDialog;
